import React from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Close as CloseIcon, WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import {
  accentInk,
  contrastTextColor,
  type AvisoPosicion,
  type TenantAppearance,
} from '../../utils/tenantAppearance';

const TENANT_AVISO_HEIGHT = 32;
const DEFAULT_AVISO_COLOR = '#d97706';

type EsquinaPosicion =
  | 'esquina-sup-izq'
  | 'esquina-sup-der'
  | 'esquina-inf-izq'
  | 'esquina-inf-der';

const ESQUINAS: EsquinaPosicion[] = [
  'esquina-sup-izq',
  'esquina-sup-der',
  'esquina-inf-izq',
  'esquina-inf-der',
];

interface TenantAvisoEntornoBannerProps {
  appearance: TenantAppearance;
  tenantName: string | null;
  visible: boolean;
  onClose?: () => void;
  /** Dentro de la vista previa: no usa posición fija ni portal. */
  preview?: boolean;
}

function esEsquina(posicion: AvisoPosicion): posicion is EsquinaPosicion {
  return (ESQUINAS as string[]).includes(posicion);
}

function textoCompleto(etiqueta: string | null, tenantName: string | null): string {
  return `Estás operando en ${etiqueta?.trim() || 'esta empresa'}${tenantName ? ` · ${tenantName}` : ''}`;
}

function textoCinta(etiqueta: string | null, tenantName: string | null): string {
  return [etiqueta?.trim(), tenantName?.trim()].filter(Boolean).join(' · ') || 'Entorno';
}

const cintaPorEsquina: Record<
  EsquinaPosicion,
  { top?: number; bottom?: number; left?: number; right?: number; transform: string }
> = {
  'esquina-sup-izq': { top: 28, left: -48, transform: 'rotate(-45deg)' },
  'esquina-sup-der': { top: 28, right: -48, transform: 'rotate(45deg)' },
  'esquina-inf-izq': { bottom: 28, left: -48, transform: 'rotate(45deg)' },
  'esquina-inf-der': { bottom: 28, right: -48, transform: 'rotate(-45deg)' },
};

const cajaPorEsquina: Record<
  EsquinaPosicion,
  { top?: number; bottom?: number; left?: number; right?: number }
> = {
  'esquina-sup-izq': { top: 0, left: 0 },
  'esquina-sup-der': { top: 0, right: 0 },
  'esquina-inf-izq': { bottom: 0, left: 0 },
  'esquina-inf-der': { bottom: 0, right: 0 },
};

const TenantAvisoEntornoBanner: React.FC<TenantAvisoEntornoBannerProps> = ({
  appearance,
  tenantName,
  visible,
  onClose,
  preview = false,
}) => {
  if (!visible) return null;

  const color = appearance.colorAcento ?? DEFAULT_AVISO_COLOR;
  const textColor = contrastTextColor(color);
  const ink = accentInk(color);
  const opacidad = appearance.avisoOpacidad / 100;
  const fondo = alpha(color, opacidad);
  const mensaje = textoCompleto(appearance.etiqueta, tenantName);
  const posicion = appearance.avisoPosicion;

  const cerrar = onClose ? (
    <Tooltip title="Ocultar aviso en esta pestaña">
      <IconButton
        size="small"
        aria-label="Ocultar aviso"
        onClick={onClose}
        sx={{
          color: 'inherit',
          p: 0.25,
          flexShrink: 0,
          '&:hover': { bgcolor: 'rgba(0,0,0,0.12)' },
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  ) : null;

  if (!esEsquina(posicion)) {
    const abajo = posicion === 'franja-inf';
    const fijaAbajo = abajo && !preview;
    const barra = (
      <Box
        role="status"
        className="app-tenant-aviso"
        sx={{
          position: fijaAbajo ? 'fixed' : abajo ? 'absolute' : preview ? 'relative' : 'absolute',
          top: !abajo && !preview ? '100%' : undefined,
          bottom: abajo ? 0 : undefined,
          left: 0,
          right: 0,
          zIndex: fijaAbajo ? (theme) => theme.zIndex.drawer + 2 : 1,
          boxShadow: abajo
            ? `0 -2px 6px ${alpha('#000', 0.18 * opacidad)}`
            : `0 2px 6px ${alpha('#000', 0.18 * opacidad)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          height: TENANT_AVISO_HEIGHT,
          pl: 2,
          pr: onClose ? 6 : 2,
          boxSizing: 'border-box',
          bgcolor: fondo,
          color: textColor,
          borderTop: ink !== color ? `1px solid ${alpha(ink, 0.35 * opacidad)}` : undefined,
          borderBottom: ink !== color ? `1px solid ${alpha(ink, opacidad)}` : undefined,
        }}
      >
        <WarningAmberIcon fontSize="small" sx={{ flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
          {mensaje}
        </Typography>
        {onClose && (
          <Box sx={{ position: 'absolute', right: 8, display: 'flex' }}>{cerrar}</Box>
        )}
      </Box>
    );
    if (fijaAbajo && typeof document !== 'undefined') return createPortal(barra, document.body);
    return barra;
  }

  const caja = cajaPorEsquina[posicion];
  const cinta = cintaPorEsquina[posicion];
  const esquina = (
    <Box
      role="status"
      className="app-tenant-aviso"
      sx={{
        position: preview ? 'absolute' : 'fixed',
        ...caja,
        width: 150,
        height: 150,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: (theme) => theme.zIndex.drawer + 2,
      }}
    >
      <Box
        title={mensaje}
        sx={{
          position: 'absolute',
          ...cinta,
          width: 220,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          px: 1,
          boxSizing: 'border-box',
          bgcolor: fondo,
          color: textColor,
          boxShadow: `0 2px 6px ${alpha('#000', 0.28 * opacidad)}`,
          pointerEvents: 'auto',
        }}
      >
        <Typography
          component="span"
          sx={{ fontWeight: 800, fontSize: 12, letterSpacing: 0.4, lineHeight: 1, minWidth: 0 }}
          noWrap
        >
          {textoCinta(appearance.etiqueta, tenantName)}
        </Typography>
        {cerrar}
      </Box>
    </Box>
  );

  if (preview || typeof document === 'undefined') return esquina;
  return createPortal(esquina, document.body);
};

export default TenantAvisoEntornoBanner;
